import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import Icon from '../../../../../components/common/Icon';
import { ReactComponent as MinusIcon } from '../../../../../icons/minus.svg';
import { ReactComponent as PlusIcon } from '../../../../../icons/plus.svg';
import { getSetting, setSetting, settingKeys } from '../../../../../utils/settings';

import './FontSizePlugin.scss';

const MOBILE_MAX_FONT_SIZE = '40px';
const MOBILE_BREAKPOINT = 992;
const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '32px', '36px', '40px', '48px', '60px', '72px'];
const sizeLabels = {
  '12px': '12',
  '14px': '14',
  '16px': '16',
  '18px': '18',
  '20px': '20',
  '24px': '24',
  '32px': '32',
  '36px': '36',
  '40px': '40',
  '48px': '48',
  '60px': '60',
  '72px': '72',
};

const FontSizePlugin = ({ target }) => {
  const { t } = useTranslation();
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    let saved = getSetting(settingKeys.fontSize);

    if (saved) {
      if (window.innerWidth < MOBILE_BREAKPOINT && fontSizes.indexOf(saved) > fontSizes.indexOf(MOBILE_MAX_FONT_SIZE)) {
        saved = MOBILE_MAX_FONT_SIZE;
      }

      setCurrentFontSize(saved);
    }

    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && fontSizes.indexOf(currentFontSize) > fontSizes.indexOf(MOBILE_MAX_FONT_SIZE)) {
      setCurrentFontSize(MOBILE_MAX_FONT_SIZE);
      setLocalCurrentFontSize(MOBILE_MAX_FONT_SIZE);
      emitFontSizeEvent(MOBILE_MAX_FONT_SIZE);
    }
  }, [isMobile, currentFontSize]);

  const setLocalCurrentFontSize = (fontSize) => {
    setSetting(settingKeys.fontSize, fontSize);
  };

  const emitFontSizeEvent = (fontSize) => {
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('fontSizeChanged', { detail: { fontSize } }));
  };

  const handleIncreaseFontSize = () => {
    let index = fontSizes.indexOf(currentFontSize);
    const nextSize = fontSizes[index + 1];

    if (!nextSize) return; // last item
    if (isMobile && fontSizes.indexOf(nextSize) > fontSizes.indexOf(MOBILE_MAX_FONT_SIZE)) return;

    setLocalCurrentFontSize(nextSize);
    setCurrentFontSize(nextSize);
    emitFontSizeEvent(nextSize);
  };

  const handleDecreaseFontSize = () => {
    let index = fontSizes.indexOf(currentFontSize);
    const previousSize = fontSizes[index - 1];

    if (!previousSize) return; // first item

    setLocalCurrentFontSize(previousSize);
    setCurrentFontSize(previousSize);
    emitFontSizeEvent(previousSize);
  };

  const isAtMinSize = () => {
    return fontSizes.indexOf(currentFontSize) === 0;
  };

  const isAtMaxSize = () => {
    const maxSize = isMobile ? MOBILE_MAX_FONT_SIZE : fontSizes[fontSizes.length - 1];

    return fontSizes.indexOf(currentFontSize) >= fontSizes.indexOf(maxSize);
  };

  const decreaseBtnClasses = classNames('btn-decrease-value', { disabled: isAtMinSize() });
  const increaseBtnClasses = classNames('btn-increase-value', { disabled: isAtMaxSize() });

  return (
    <div className="song-plugin font-size-plugin">
      <span className="plugin__title">{t('view.textSize')}</span>
      <span onClick={!isAtMinSize() ? handleDecreaseFontSize : undefined} className={decreaseBtnClasses}>
        <Icon icon={<MinusIcon />} />
      </span>
      <span className="display-value">{sizeLabels[currentFontSize]}</span>
      <span onClick={!isAtMaxSize() ? handleIncreaseFontSize : undefined} className={increaseBtnClasses}>
        <Icon icon={<PlusIcon />} />
      </span>
    </div>
  );
};

export default FontSizePlugin;
